/**
 * Laptop and mobile recordings are separate clients. This module only decides whether THIS
 * device's recorder should wait, keep running, or start. The other device's connection is
 * intentionally not an input: a phone disconnect must not stop a healthy laptop recorder.
 *
 * The recorder used to be created once, the retry timer cleared before MediaRecorder.start(),
 * and any throw (ended microphone track, camera not producing frames yet, unsupported stream)
 * abandoned laptop recording for the rest of the exam. Violation snapshots kept working because
 * they grab a frame from the live <video> element and never touch MediaRecorder.
 */

export function recordingDevice(isMobile) {
  return isMobile ? 'mobile' : 'laptop';
}

export function cameraStreamFromList(streams) {
  if (!streams || !streams.length) return null;
  const first = streams[0];
  if (first && typeof first.getVideoTracks === 'function') return first;
  if (first && first.stream && typeof first.stream.getVideoTracks === 'function') return first.stream;
  return null;
}

export function liveVideoTrack(stream) {
  const tracks = stream && typeof stream.getVideoTracks === 'function' ? stream.getVideoTracks() : [];
  return tracks.find((track) => track && track.readyState === 'live') || null;
}

/**
 * Clones a live mic track onto the canvas stream. An ended track, or a clone/addTrack failure,
 * records video only — it must not abort the recording.
 */
export function attachLiveAudio(canvasStream, cameraStream) {
  const tracks = cameraStream && typeof cameraStream.getAudioTracks === 'function' ? cameraStream.getAudioTracks() : [];
  const audio = tracks.find((track) => track && track.readyState === 'live');
  if (!audio) return { attached: false };
  try {
    canvasStream.addTrack(audio.clone());
    return { attached: true };
  } catch (error) {
    return { attached: false, error };
  }
}

/**
 * Firefox reports isTypeSupported('video/webm;codecs=vp8') and then throws on start() when an
 * audio track is present: that mime type has no audio codec. Video can still be recorded.
 */
export function audioBlocksRecording(error) {
  if (!error || error.name !== 'NotSupportedError') return false;
  return /audio track/i.test(String(error.message || ''));
}

export function removeAudioTracks(stream) {
  if (!stream || typeof stream.getAudioTracks !== 'function' || typeof stream.removeTrack !== 'function') return false;
  const tracks = stream.getAudioTracks();
  tracks.forEach((track) => {
    try { stream.removeTrack(track); } catch (_) { /* already removed */ }
    try { if (typeof track.stop === 'function') track.stop(); } catch (_) { /* already ended */ }
  });
  return tracks.length > 0;
}

/** If start() rejects the mic, drop audio and start once more so the video file is still saved. */
export function startRecorderOrVideoOnly({ create, stream, onAudioDropped }) {
  try {
    return { recorder: create(), videoOnly: false };
  } catch (error) {
    if (!audioBlocksRecording(error) || !removeAudioTracks(stream)) throw error;
    if (onAudioDropped) onAudioDropped(error);
    return { recorder: create(), videoOnly: true };
  }
}

/** Default: one missed 60s rotation + margin before a "healthy" recorder is treated as stalled. */
export const RECORDING_STALL_MS = 90000;

/** After hide→visible, only soft-restart if no good chunk in this window (avoids restart on brief blur). */
export const RECORDING_VISIBILITY_RESTART_MS = 30000;

/** Uploadable chunk threshold used by ExamRoom (bytes). */
export const RECORDING_GOOD_CHUNK_BYTES = 1000;

/**
 * Whether chunk production is still healthy.
 * When stall inputs are omitted, returns 'ok' so existing callers keep prior behavior.
 * Before the first good chunk, pass recorderStartedAt so a never-producing recorder can stall.
 *
 * @returns {'ok'|'stalled'}
 */
export function recordingChunkHealth({
  lastGoodChunkAt = null,
  recorderStartedAt = null,
  now = Date.now(),
  stallMs = RECORDING_STALL_MS,
} = {}) {
  if (lastGoodChunkAt == null && recorderStartedAt == null) return 'ok';
  const anchor = lastGoodChunkAt != null ? lastGoodChunkAt : recorderStartedAt;
  if (anchor == null || !Number.isFinite(stallMs) || stallMs <= 0) return 'ok';
  return (now - anchor) > stallMs ? 'stalled' : 'ok';
}

/**
 * Count empty (0) and tiny (< goodBytes) chunks toward a rebuild. Good chunks reset the counter.
 * @returns {{ consecutiveBad: number, shouldRebuild: boolean }}
 */
export function recordingBadChunkProgress(size, consecutiveBad = 0, {
  goodBytes = RECORDING_GOOD_CHUNK_BYTES,
  rebuildAfter = 2,
} = {}) {
  const bytes = size || 0;
  if (bytes >= goodBytes) return { consecutiveBad: 0, shouldRebuild: false };
  const next = consecutiveBad + 1;
  return { consecutiveBad: next, shouldRebuild: next >= rebuildAfter };
}

/**
 * Soft-restart after hide→visible only when recording looks unhealthy (no recent good chunk
 * or overlay play failed). Healthy foreground recording is left alone.
 */
export function shouldRestartRecordingAfterVisible({
  lastGoodChunkAt = null,
  now = Date.now(),
  recentMs = RECORDING_VISIBILITY_RESTART_MS,
  overlayPlayFailed = false,
} = {}) {
  if (overlayPlayFailed) return true;
  if (lastGoodChunkAt == null) return true;
  return (now - lastGoodChunkAt) >= recentMs;
}

/**
 * @returns {'keep'|'wait'|'stop-and-wait'|'start'|'restart'}
 * keep: a recorder is running against a live camera — do not build another one.
 * wait: nothing to record yet (no registration, or no live video track).
 * stop-and-wait: a recorder is running but its camera is gone.
 * start: create a recorder.
 * restart: the bound camera track ended, or a recorder exists but is not healthy, and a live track is available.
 *
 * Optional stall fields: when a recorder looks healthy but has produced no good chunk for stallMs,
 * return 'restart' instead of 'keep'. Omit stall fields for backward-compatible behavior.
 */
export function recordingStartDecision({
  recorderState = null,
  hasRegistration = false,
  hasLiveVideo = false,
  sourceTrackLive = true,
  lastGoodChunkAt = undefined,
  recorderStartedAt = undefined,
  now = undefined,
  stallMs = undefined,
} = {}) {
  const healthy = recorderState === 'recording' || recorderState === 'paused';
  if (healthy && sourceTrackLive && hasLiveVideo && hasRegistration) {
    const stallInputsProvided = lastGoodChunkAt !== undefined || recorderStartedAt !== undefined;
    if (stallInputsProvided) {
      const health = recordingChunkHealth({
        lastGoodChunkAt: lastGoodChunkAt ?? null,
        recorderStartedAt: recorderStartedAt ?? null,
        now: now !== undefined ? now : Date.now(),
        stallMs: stallMs !== undefined ? stallMs : RECORDING_STALL_MS,
      });
      if (health === 'stalled') return 'restart';
    }
    return 'keep';
  }
  if (!hasRegistration || !hasLiveVideo) return healthy ? 'stop-and-wait' : 'wait';
  if (healthy || !sourceTrackLive) return 'restart';
  return 'start';
}

/** Server accepts video_YYYY-MM-DD_HH-mm-ss.webm and an optional _N suffix (same second must not collide). */
export function nextChunkFilename(used, date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  const base = `video_${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(date.getSeconds())}`;
  let name = `${base}.webm`;
  let n = 1;
  while (used.has(name) && n <= 999) {
    name = `${base}_${n}.webm`;
    n += 1;
  }
  used.add(name);
  return name;
}
